// EnergyMatchTab.tsx
import React, { useState } from "react";
import { Zap } from "lucide-react";
import AstriaUKV2Shell from "../AstriaUKV2Shell";
import { useUKV2Colors } from "../AstriaUKV2ThemeContext";
import {
  AstriaUKV2ResultCard,
  AstriaUKV2FieldBlock,
  AstriaUKV2ActionList,
  UKV2LoadingState,
  UKV2ErrorState,
  UKV2ChatCta,
} from "../AstriaUKV2Card";
import {
  UKV2PersonFields,
  UKV2SubmitButton,
  PersonDetails,
  EMPTY_PERSON_DETAILS,
} from "../AstriaUKV2Wizard";
import { useAstriaUKV2Tab } from "../useAstriaUKV2Tab";
import { AstriaUKV2EnergyMatchData, Message } from "../../../../contexts/ChatContext";

const SUBCATEGORY_NAME = "Energy Match";

const buildMessage = (self: PersonDetails, partner: PersonDetails) => `
Read our Energy Match (analytical read of the dynamic between us).
My Date of Birth: ${self.dob.trim()}.
My Time of Birth: ${self.time.trim() || "Not provided"}.
My Place of Birth: ${self.place.trim() || "Not provided"}.
${partner.name.trim() || "My partner"}'s Date of Birth: ${partner.dob.trim()}.
${partner.name.trim() || "My partner"}'s Time of Birth: ${partner.time.trim() || "Not provided"}.
${partner.name.trim() || "My partner"}'s Place of Birth: ${partner.place.trim() || "Not provided"}.
Please give me the calm-analytical read of our connection.
`.trim();

// Colors must be read INSIDE AstriaUKV2Shell's subtree (see the note in
// ZodiacPersonalityTab.tsx) — these small components are rendered as
// `children`, not evaluated inline, so their hook calls resolve after the
// shell's theme provider has mounted.

const ResultView: React.FC<{
  data?: AstriaUKV2EnergyMatchData;
  message: Message | null;
  reset: () => void;
  goToChat: () => Promise<void>;
}> = ({ data, message, reset, goToChat }) => {
  const { colors } = useUKV2Colors();

  if (data) {
    return (
      <>
        <AstriaUKV2ResultCard
          icon={<Zap className="w-5 h-5" />}
          title="Energy Match"
          subtitle="Analytical + emotional read"
          shareText={`${data.opening}\n\n${data.current_energy}\n\n${data.where_this_can_go}`}
        >
          <AstriaUKV2FieldBlock label="Opening" value={data.opening} />
          <AstriaUKV2FieldBlock label="Current Energy" value={data.current_energy} />
          <AstriaUKV2FieldBlock label="Connection Pattern" value={data.connection_pattern} />
          <AstriaUKV2FieldBlock label="Gap Analysis" value={data.gap_analysis} />
          <AstriaUKV2ActionList label="Today" items={data.heart_action_plan?.today || []} />
          <AstriaUKV2ActionList label="This Week" items={data.heart_action_plan?.this_week || []} />
          <AstriaUKV2FieldBlock label="Where This Can Go" value={data.where_this_can_go} />
        </AstriaUKV2ResultCard>
        <button
          onClick={reset}
          className="text-xs font-semibold transition-colors"
          style={{ marginTop: 14, color: colors.mutedColor }}
        >
          Start a New Reading
        </button>
        <UKV2ChatCta onClick={goToChat} />
      </>
    );
  }

  if (message) {
    return (
      <AstriaUKV2ResultCard icon={<Zap className="w-5 h-5" />} title="Energy Match">
        <p
          style={{
            fontSize: 15,
            lineHeight: 1.75,
            color: colors.bodyColor,
            whiteSpace: "pre-wrap",
          }}
        >
          {message.content}
        </p>
      </AstriaUKV2ResultCard>
    );
  }

  return null;
};

const IntroForm: React.FC<{
  self: PersonDetails;
  setSelf: (v: PersonDetails) => void;
  partner: PersonDetails;
  setPartner: (v: PersonDetails) => void;
  onSubmit: () => void;
  formValid: boolean;
}> = ({ self, setSelf, partner, setPartner, onSubmit, formValid }) => {
  const { colors } = useUKV2Colors();

  return (
    <>
      <div style={{ marginBottom: 20 }}>
        <h2
          style={{
            fontSize: 21,
            fontWeight: 700,
            color: colors.titleColor,
            marginBottom: 6,
          }}
        >
          Energy Match
        </h2>
        <p style={{ fontSize: 13.5, color: colors.mutedColor, lineHeight: 1.6 }}>
          A calm, analytical read of the dynamic between you and someone else — not a light
          assessment, a proper look at how you fit.
        </p>
      </div>

      <UKV2PersonFields
        heading="Your Details"
        values={self}
        onChange={setSelf}
        namePlaceholder="Your name"
      />
      <UKV2PersonFields heading="Their Details" values={partner} onChange={setPartner} />

      <UKV2SubmitButton onClick={onSubmit} disabled={!formValid} label="Reveal Our Energy Match" />
    </>
  );
};

const EnergyMatchTab: React.FC = () => {
  const { loading, error, message, send, reset, goToChat } = useAstriaUKV2Tab({
    subCategoryName: SUBCATEGORY_NAME,
  });

  const [self, setSelf] = useState<PersonDetails>(EMPTY_PERSON_DETAILS);
  const [partner, setPartner] = useState<PersonDetails>(EMPTY_PERSON_DETAILS);

  const started = !!message || loading || !!error;
  const formValid = !!self.dob.trim() && !!partner.dob.trim();

  const handleSubmit = () => send(buildMessage(self, partner));

  const data = message?.astriaUKV2Data as AstriaUKV2EnergyMatchData | undefined;

  if (started) {
    return (
      <AstriaUKV2Shell active="energy-match">
        {loading && <UKV2LoadingState label="Reading the dynamic between you two…" />}
        {!loading && error && <UKV2ErrorState message={error} onRetry={handleSubmit} />}
        {!loading && !error && (
          <ResultView data={data} message={message} reset={reset} goToChat={goToChat} />
        )}
      </AstriaUKV2Shell>
    );
  }

  return (
    <AstriaUKV2Shell active="energy-match">
      <IntroForm
        self={self}
        setSelf={setSelf}
        partner={partner}
        setPartner={setPartner}
        onSubmit={handleSubmit}
        formValid={formValid}
      />
    </AstriaUKV2Shell>
  );
};

export default EnergyMatchTab;
